import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderComercioService } from 'src/app/services/order-comercio.service';
import { OrdenComercio } from 'src/app/models/OrdenComercio';
import { ProductoOrdenComercio } from 'src/app/models/ProductoOrdenComercio';
import { VademecumOrdenComercio } from 'src/app/models/VademecumOrdenComercio';
import { EstadoOrden } from 'src/app/models/EstadoOrden';

// Fila unificada para mostrar productos y medicamentos (vademécums) en la
// misma lista — mismo criterio de cálculo que resumen-order.component.ts:
// precio unitario, % de descuento, precio final (ya viene calculado al
// momento de la compra) y subtotal = precio final x cantidad.
interface ItemComprado {
  nombre: string;
  detalle?: string;
  cantidad: number;
  precio: number;
  porcentajeDescuento: number;
  precioFinal: number;
  subtotal: number;
}

@Component({
    selector: 'app-more-orden-comercio-detalle',
    templateUrl: './more-orden-comercio-detalle.component.html',
    styleUrls: ['./more-orden-comercio-detalle.component.css'],
    standalone: false
})
export class MoreOrdenComercioDetalleComponent implements OnInit {
  private _authService = inject(AuthService);
  private _orderComercioService = inject(OrderComercioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private _aRoute = inject(ActivatedRoute);

  id = Number(this._aRoute.snapshot.paramMap.get('id'));
  orden = signal<OrdenComercio | null>(null);
  estadosOrdenes = signal<EstadoOrden[]>([]);
  items = signal<ItemComprado[]>([]);
  // Estado elegido en el combo, todavía no guardado — cambiar la orden es
  // un paso de dos tiempos: elegir y después apretar "Guardar".
  estadoSeleccionado = signal<number | null>(null);
  error = signal<string>('');
  message = signal<string>('');
  guardando = signal<boolean>(false);

  total = computed(() => this.items().reduce((acc, i) => acc + i.subtotal, 0));
  ahorro = computed(() => this.items().reduce((acc, i) => acc + (i.precio - i.precioFinal) * i.cantidad, 0));

  get hayCambiosSinGuardar(): boolean {
    return this.estadoSeleccionado() !== null && this.estadoSeleccionado() !== this.orden()?.id_estado_orden;
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    if (!this.id) {
      this.router.navigate(['/more-ordenes-comercio']);
      return;
    }

    try {
      await this._loadingService.run(async () => {
        await this._authService.ensureSession();
        this.estadosOrdenes.set(await firstValueFrom(this._orderComercioService.getEstadosOrdenes()));

        const res = await firstValueFrom(this._orderComercioService.getOrdenComercio(this.id));
        if (!res) {
          this.error.set('No se encontró la orden.');
          return;
        }
        this.orden.set(res);
        this.estadoSeleccionado.set(res.id_estado_orden ?? null);

        const [productos, vademecums] = await Promise.all([
          firstValueFrom(this._orderComercioService.getProductosXOrdenComercio(this.id)),
          firstValueFrom(this._orderComercioService.getVademecumsXOrdenComercio(this.id)),
        ]);

        this.items.set([
          ...(productos ?? []).map((p: ProductoOrdenComercio) => this.aItem(p.x_nombre, undefined, p.n_cantidad, p.n_precio, p.n_porcentaje_descuento, p.n_precio_final)),
          ...(vademecums ?? []).map((v: VademecumOrdenComercio) => this.aItem(v.x_nombre, [v.x_presentacion, v.nombreLaboratorio].filter(Boolean).join(' · '), v.n_cantidad, v.n_precio, v.n_porcentaje_descuento, v.n_precio_final)),
        ]);
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar la orden.');
    }
  }

  private aItem(
    nombre: string | undefined,
    detalle: string | undefined,
    cantidad: number | undefined,
    precio: number | undefined,
    porcentajeDescuento: number | undefined,
    precioFinal: number | undefined
  ): ItemComprado {
    const cant = cantidad ?? 0;
    const prec = Number(precio) || 0;
    // n_precio_final ya viene calculado al momento de la compra; si por algún
    // motivo no está, se recalcula con el % de descuento (mismo criterio que
    // order-comercio/resumen-order.component.ts).
    const precFinal = precioFinal != null ? Number(precioFinal) : prec * (1 - (Number(porcentajeDescuento) || 0) / 100);
    return {
      nombre: nombre || 'Sin nombre',
      detalle,
      cantidad: cant,
      precio: prec,
      porcentajeDescuento: Number(porcentajeDescuento) || 0,
      precioFinal: precFinal,
      subtotal: precFinal * cant,
    };
  }

  onEstadoSeleccionadoChange(value: string): void {
    this.estadoSeleccionado.set(Number(value));
  }

  async guardarEstado(): Promise<void> {
    const actual = this.orden();
    const id_estado_orden = this.estadoSeleccionado();
    if (!actual || !id_estado_orden || id_estado_orden === actual.id_estado_orden) return;

    this.guardando.set(true);
    this.error.set('');

    try {
      await firstValueFrom(this._orderComercioService.updateEstadoOrdenComercio(this.id, id_estado_orden));
      this.orden.set({
        ...actual,
        id_estado_orden,
        estadoOrden: this.estadosOrdenes().find(e => e.id === id_estado_orden)?.x_descripcion ?? actual.estadoOrden,
      });
      this.message.set('Estado actualizado correctamente');
      setTimeout(() => this.message.set(''), 2500);
    } catch (e) {
      console.error(e);
      this.estadoSeleccionado.set(actual.id_estado_orden ?? null);
      this.error.set('No se pudo actualizar el estado.');
    } finally {
      this.guardando.set(false);
    }
  }

  get formaEntrega(): string {
    const o = this.orden();
    if (!o) return '-';
    if (o.b_retiro_local) return 'Retiro en el local';
    return `A domicilio: ${o.x_envio_direccion || o.direccionComercio || 'sin dirección registrada'}`;
  }

  get formaPago(): string {
    const o = this.orden();
    if (!o) return '-';
    return o.b_pago_efectivo ? 'Efectivo' : 'Mercado Pago';
  }

  back(): void {
    this._location.back();
  }
}
